import { useEffect, useMemo, useState } from 'react'
import { Button, Modal, Progress, Radio, Space, Spin, Table, Typography, Upload } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, DownloadOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons'
import { useToast } from '../contexts/ToastContext'
import {
  useBulkUserUploadStatus,
  useDownloadUsersMutation,
  useUploadUsersMutation,
} from '../hooks/useUsers'
import { fileToBase64 } from '../lib/files'
import type { BulkUserUploadResultRow } from '../types/users'

type ManageUsersModalProps = {
  open: boolean
  clientId: string
  onClose: () => void
  onCompleted?: () => void
}

const acceptedExtensions = ['.csv', '.xlsx']
const xlsxContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function downloadBase64Csv(base64: string) {
  const link = document.createElement('a')
  link.href = `data:text/csv;base64,${base64}`
  link.download = `users_${Date.now()}.csv`
  link.click()
  link.remove()
}

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 Bytes'
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** index
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`
}

function formatDateTime(value: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

function getUploadContentType(file: File) {
  const lowerName = file.name.toLowerCase()
  if (lowerName.endsWith('.csv')) return 'text/csv'
  if (lowerName.endsWith('.xlsx')) return xlsxContentType
  return file.type
}

export default function ManageUsersModal({ open, clientId, onClose, onCompleted }: ManageUsersModalProps) {
  const { addToast } = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [queueId, setQueueId] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [terminalToastSent, setTerminalToastSent] = useState(false)
  const [inviteNewUsers, setInviteNewUsers] = useState(false)

  const downloadMutation = useDownloadUsersMutation()
  const uploadMutation = useUploadUsersMutation()
  const statusQuery = useBulkUserUploadStatus(queueId, open && isUploading && !!queueId)
  const uploadStatus = queueId ? statusQuery.data?.data ?? null : null

  useEffect(() => {
    if (!open) {
      setFile(null)
      setQueueId('')
      setIsUploading(false)
      setUploadSuccess(false)
      setTerminalToastSent(false)
      setInviteNewUsers(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !isUploading || !queueId) return
    if (!uploadStatus) return
    if (uploadStatus.status !== 'Completed' && uploadStatus.status !== 'Failed') return
    setIsUploading(false)
    setUploadSuccess(true)
    if (terminalToastSent) return

    setTerminalToastSent(true)
    if (uploadStatus.status === 'Completed') {
      const hasErrors = uploadStatus.results.some((row) => row.status !== 1)
      addToast(hasErrors ? 'User upload completed with errors' : 'User upload completed successfully', hasErrors ? 'warning' : 'success')
    } else {
      addToast('User upload completed with errors', 'warning')
    }
    onCompleted?.()
  }, [addToast, isUploading, onCompleted, open, queueId, terminalToastSent, uploadStatus])

  useEffect(() => {
    if (!statusQuery.isError) return
    setIsUploading(false)
    addToast(statusQuery.error instanceof Error ? statusQuery.error.message : 'Failed to get upload status', 'error')
  }, [addToast, statusQuery.error, statusQuery.isError])

  const columns: ColumnsType<BulkUserUploadResultRow> = useMemo(
    () => [
      { title: 'Row', dataIndex: 'row', key: 'row', width: 80 },
      { title: 'User ID', dataIndex: 'userId', key: 'userId', render: (value: string) => value || 'N/A' },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (value: number) => (
          <span className={value === 1 ? 'font-semibold text-green-700' : 'font-semibold text-red-700'}>
            {value === 1 ? 'Success' : 'Error'}
          </span>
        ),
      },
      { title: 'Message', dataIndex: 'message', key: 'message' },
    ],
    []
  )

  const beforeUpload = (nextFile: File) => {
    const lowerName = nextFile.name.toLowerCase()
    if (!acceptedExtensions.some((extension) => lowerName.endsWith(extension))) {
      addToast('Please upload a CSV or XLSX file', 'error')
      return Upload.LIST_IGNORE
    }
    setFile(nextFile)
    return false
  }

  const handleDownload = async () => {
    if (!clientId) {
      addToast('No active client selected', 'warning')
      return
    }

    try {
      const base64 = await downloadMutation.mutateAsync(clientId)
      if (!base64) {
        addToast('Users download returned no data', 'warning')
        return
      }
      downloadBase64Csv(base64)
      addToast('Users list downloaded successfully', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to download users list. Please try again.', 'error')
    }
  }

  const handleUpload = async () => {
    if (!file) {
      addToast('Please select a file to upload', 'warning')
      return
    }
    if (!clientId) {
      addToast('No active client selected', 'warning')
      return
    }

    try {
      setTerminalToastSent(false)
      setUploadSuccess(false)
      setIsUploading(true)
      const formFile = await fileToBase64(file)
      const response = await uploadMutation.mutateAsync({
        formFile,
        contentType: getUploadContentType(file),
        clientId,
        inviteNewUsers,
      })

      if (!response.success || !response.bulkUserUpdateQueueId) {
        setIsUploading(false)
        addToast(response.message || 'Upload failed', 'error')
        return
      }

      setQueueId(response.bulkUserUpdateQueueId)
      addToast(response.message || 'Upload queued successfully', 'success')
    } catch (error) {
      setIsUploading(false)
      addToast(error instanceof Error ? error.message : 'Failed to upload users list. Please try again.', 'error')
    }
  }

  const confirmUpload = () => {
    if (!inviteNewUsers) {
      void handleUpload()
      return
    }
    Modal.confirm({
      title: 'Invite new users?',
      content: 'New users in this file will be emailed an invite as soon as they are created. Existing users are not re-invited.',
      okText: 'Upload and invite',
      onOk: () => void handleUpload(),
    })
  }

  const removeFile = () => setFile(null)

  return (
    <Modal
      open={open}
      title="Manage Users"
      onCancel={onClose}
      footer={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            loading={downloadMutation.isPending}
            onClick={() => void handleDownload()}
          >
            Download Users Data
          </Button>
          {!uploadSuccess && !isUploading && (
            <Button
              type="primary"
              icon={<UploadOutlined />}
              disabled={!file}
              loading={uploadMutation.isPending}
              onClick={confirmUpload}
            >
              Upload
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
      width={880}
      destroyOnClose
    >
      {!isUploading && !uploadSuccess && (
        <div className="space-y-4">
          <Upload.Dragger
            accept=".csv,.xlsx"
            beforeUpload={beforeUpload}
            showUploadList={false}
            multiple={false}
            disabled={uploadMutation.isPending}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drag and drop Users CSV or Excel file here</p>
            <p className="ant-upload-hint">or click to browse for file</p>
          </Upload.Dragger>

          <Typography.Text type="secondary" className="block text-sm">
            Use Download Users Data as the template. Separate multiple User Groups with a semicolon, e.g. Sales; Admins.
          </Typography.Text>

          {file && (
            <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div>
                <Typography.Text strong>{file.name}</Typography.Text>
                <div className="text-sm text-gray-500">{formatBytes(file.size)}</div>
              </div>
              <Button type="text" icon={<DeleteOutlined />} aria-label="Remove file" onClick={removeFile} />
            </div>
          )}

          <div>
            <Typography.Text strong>Invite new users automatically?</Typography.Text>
            <div className="text-sm text-gray-500">If not, new users are added with For Invite status and can be invited later.</div>
            <Radio.Group
              className="mt-2"
              value={inviteNewUsers}
              onChange={(event) => setInviteNewUsers(event.target.value)}
              options={[
                { label: 'No', value: false },
                { label: 'Yes', value: true },
              ]}
            />
          </div>
        </div>
      )}

      {isUploading && !uploadStatus && (
        <div className="flex min-h-56 flex-col items-center justify-center gap-3">
          <Spin size="large" />
          <Typography.Text type="secondary">
            Uploading users file and preparing status...
          </Typography.Text>
        </div>
      )}

      {(isUploading || uploadSuccess) && uploadStatus && (
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm text-gray-500">Status</div>
                <div className="text-lg font-semibold">{uploadStatus.status}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Progress</div>
                <div className="text-lg font-semibold">{uploadStatus.completionPercentage}%</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Client</div>
                <div className="font-medium">{uploadStatus.client || '—'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Process User</div>
                <div className="font-medium">{uploadStatus.processUser || '—'}</div>
              </div>
              {uploadStatus.processStartDate && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-500">Started</div>
                  <div className="font-medium">{formatDateTime(uploadStatus.processStartDate)}</div>
                </div>
              )}
            </div>
            <div className="mt-4">
              <Progress percent={uploadStatus.completionPercentage} status={uploadStatus.status === 'Failed' ? 'exception' : undefined} />
            </div>
          </div>

          {uploadStatus.results.length > 0 && (
            <Table<BulkUserUploadResultRow>
              rowKey={(row) => `${row.row}-${row.userId}-${row.message}`}
              columns={columns}
              dataSource={uploadStatus.results}
              pagination={false}
              size="small"
              scroll={{ y: 320 }}
            />
          )}

          {uploadSuccess && !isUploading && (
            <Typography.Text type="secondary">
              The file has been processed. You can now close this modal.
            </Typography.Text>
          )}
        </div>
      )}
    </Modal>
  )
}
